import React, { createContext, useState, useEffect, useContext, ReactNode } from "react";
import { jwtDecode } from "jwt-decode";
import { tokenStorage } from "@/api/tokenManager";
import { User } from "@/types/users.types";
import { UserFormApiService } from "@/services/user.service";
import { AuthApi } from "@/services/auth.service";
import { startIdleWatcher } from "@/auth/idleSession";
import { prefetchHomeIfThere } from '@/services/homePrefetch';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  isImpersonating: boolean;
  logout: () => void;
  checkAuth: () => Promise<void>; 
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface JwtPayload {
  sub: string;
  /** Set on a "Log in as" token; the server is the source of truth, not browser storage. */
  is_impersonated?: boolean;
}

/** Whether the stored access token is a "Log in as" one. A leftover browser flag from a session
 *  that ended without "Return to my account" (expired, signed out elsewhere) is cleared. */
export const tokenIsImpersonated = (token: string | null | undefined): boolean => {
  let imp = false;
  if (token) {
    try { imp = jwtDecode<JwtPayload>(token).is_impersonated === true; } catch { imp = false; }
  }
  if (!imp) {
    localStorage.removeItem("is_impersonating");
    localStorage.removeItem("original_admin_name");
  }
  return imp;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isImpersonating, setIsImpersonating] = useState<boolean>(() =>
    tokenIsImpersonated(tokenStorage.getAccessToken())
  );

  const initializeAuth = async () => {
    const token = tokenStorage.getAccessToken();
    const impFlag = tokenIsImpersonated(token);

    if (!token) {
      setIsLoading(false);
      setIsImpersonating(false);
      return;
    }

    try {
      const decodedToken = jwtDecode<JwtPayload>(token);
      const userId = decodedToken.sub;

      // On the home page, ask for its layout + widget list now, so they go
      // in the same /batch call as the user (see homePrefetch.ts).
      prefetchHomeIfThere();

      const userData = await UserFormApiService.getItem!(userId);
      
      setUser(userData);
      setIsImpersonating(impFlag);

    } catch (error) {
      setUser(null);
      tokenStorage.clearTokens();
      localStorage.removeItem("is_impersonating");
      localStorage.removeItem("original_admin_name");
      setIsImpersonating(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initializeAuth();
  }, []);

  // Session idle timeout (tenant Security setting) — only while signed in.
  useEffect(() => {
    if (!user) return;
    return startIdleWatcher();
  }, [user]);

  const logout = () => {
    AuthApi.logout();
    setUser(null);
    setIsImpersonating(false);
    tokenStorage.clearTokens();
    localStorage.removeItem("is_impersonating");
    localStorage.removeItem("original_admin_name");
    localStorage.removeItem("ilearn_last_activity");
    // Redirect to root — shows guest dashboard if available, otherwise falls back to /login
    window.location.href = '/';
  };

  const value = {
    isAuthenticated: !!user,
    user,
    isLoading,
    isImpersonating,
    logout,
    checkAuth: initializeAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};